import React, { useCallback, useEffect, useMemo, useState } from "react";
import { apiGet, apiPost } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Tabs, TabsList, TabsTrigger,
} from "@/components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  TrendingUp, Minus, Loader2, RefreshCcw, Database, CheckCircle2,
  XCircle, Info, BarChart3, ArrowUp, ArrowDown, Search, AlertCircle,
} from "lucide-react";
import { toast } from "sonner";

/**
 * UsTrackRecordTab — Phase 5 US Track Record dashboard.
 *
 * Tracks every US BUY / STRONG_BUY signal vs the **S&P 500 (^GSPC)**
 * index (NOT SPY ETF). Mirrors the CSE tab field-for-field but with
 * USD currency, no voting/non-voting filter, and a US flag.
 *
 * Visible to all authenticated users; admins additionally see
 * Refresh + Rebuild buttons.
 *
 * Strict separation: this tab NEVER reads from the CSE
 * `signal_track_records` collection. All data comes from
 * `us_signal_track_records` via /api/us-track-record.
 */

const fmtPct = (v, opts = {}) => {
  if (v == null) return "—";
  const sign = v > 0 ? "+" : "";
  return `${sign}${v.toFixed(opts.decimals ?? 2)}%`;
};

const fmtUsd = (v) => {
  if (v == null) return "—";
  return `$${Number(v).toFixed(2)}`;
};

const fmtIndex = (v) => {
  if (v == null) return "—";
  return Number(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const fmtDate = (v) => {
  if (!v) return "—";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "2-digit" });
};

const normaliseSignal = (s) => (s || "").toString().toUpperCase().replace(/[\s_]+/g, "_");

const signalBadgeClass = (s) => {
  const norm = normaliseSignal(s);
  if (norm === "STRONG_BUY") return "bg-[hsl(var(--success))]/15 text-[hsl(var(--success))] border-[hsl(var(--success))]/40";
  if (norm === "BUY") return "bg-[hsl(var(--info))]/15 text-[hsl(var(--info))] border-[hsl(var(--info))]/40";
  if (norm === "WATCH") return "bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))] border-[hsl(var(--warning))]/40";
  if (norm === "HOLD" || norm === "NEUTRAL") return "bg-muted text-muted-foreground border-muted-foreground/30";
  if (norm === "AVOID" || norm === "SELL" || norm === "STRONG_SELL") return "bg-[hsl(var(--destructive))]/15 text-[hsl(var(--destructive))] border-[hsl(var(--destructive))]/40";
  return "bg-muted text-muted-foreground";
};

const VALUATION_LABEL = {
  significantly_undervalued: "Significantly Undervalued",
  undervalued: "Undervalued",
  fair_value: "Fair Value",
  fairly_valued: "Fair Value",
  overvalued: "Overvalued",
  significantly_overvalued: "Significantly Overvalued",
};
const VALUATION_BADGE_CLASS = {
  significantly_undervalued: "bg-[hsl(var(--success))]/15 text-[hsl(var(--success))] border-[hsl(var(--success))]/40",
  undervalued: "bg-[hsl(var(--success))]/10 text-[hsl(var(--success))] border-[hsl(var(--success))]/30",
  fair_value: "bg-muted text-muted-foreground border-muted-foreground/30",
  fairly_valued: "bg-muted text-muted-foreground border-muted-foreground/30",
  overvalued: "bg-[hsl(var(--warning))]/10 text-[hsl(var(--warning))] border-[hsl(var(--warning))]/30",
  significantly_overvalued: "bg-[hsl(var(--destructive))]/15 text-[hsl(var(--destructive))] border-[hsl(var(--destructive))]/40",
};

const returnClass = (v) => {
  if (v == null) return "text-muted-foreground";
  if (v > 0) return "text-[hsl(var(--success))]";
  if (v < 0) return "text-[hsl(var(--destructive))]";
  return "text-muted-foreground";
};

export const UsTrackRecordTab = ({ onSummaryLoaded }) => {
  const { isAdmin } = useAuth();
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState("active");
  const [signalFilter, setSignalFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [refreshing, setRefreshing] = useState(false);
  const [backfilling, setBackfilling] = useState(false);
  const [sortBy, setSortBy] = useState({ key: "return_pct", dir: "desc" });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ status: statusFilter, limit: "500" });
      if (signalFilter !== "all") params.append("signal", signalFilter);
      const res = await apiGet(`/us-track-record?${params.toString()}`);
      setRows(res.rows || []);
      setSummary(res.summary || null);
      onSummaryLoaded?.(res.summary || null);
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Failed to load US track record");
    } finally {
      setLoading(false);
    }
  }, [statusFilter, signalFilter, onSummaryLoaded]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let out = rows;
    if (q) {
      out = out.filter((r) => (
        (r.symbol || "").toLowerCase().includes(q) ||
        (r.company_name || "").toLowerCase().includes(q) ||
        (r.sector || "").toLowerCase().includes(q)
      ));
    }
    const { key, dir } = sortBy;
    out = [...out].sort((a, b) => {
      const av = a[key];
      const bv = b[key];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === "number" && typeof bv === "number") {
        return dir === "asc" ? av - bv : bv - av;
      }
      return dir === "asc"
        ? String(av).localeCompare(String(bv))
        : String(bv).localeCompare(String(av));
    });
    return out;
  }, [rows, search, sortBy]);

  const onSort = (key) => {
    setSortBy((prev) => prev.key === key
      ? { key, dir: prev.dir === "asc" ? "desc" : "asc" }
      : { key, dir: "desc" });
  };

  const onRefresh = async () => {
    setRefreshing(true);
    try {
      const r = await apiPost("/admin/us-track-record/refresh");
      toast.success(`Snapshot complete — ${r.new_episodes} new, ${r.updated_episodes} updated, ${r.closed_episodes} closed`);
      await fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Refresh failed");
    } finally {
      setRefreshing(false);
    }
  };

  const onBackfill = async () => {
    setBackfilling(true);
    try {
      const r = await apiPost("/admin/us-track-record/backfill?wipe=true");
      toast.success(`Backfill complete — ${r.backfill.episodes_created} episodes from ${r.backfill.symbols_scanned} symbols`);
      await fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Backfill failed");
    } finally {
      setBackfilling(false);
    }
  };

  const s = summary || {};
  return (
    <div className="space-y-6" data-testid="us-track-record-tab">
      {/* Header summary + admin controls */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="text-sm text-muted-foreground max-w-2xl">
            Every US BUY and STRONG_BUY signal we publish — tracked from the day
            it was issued vs the <span className="font-semibold text-foreground">S&amp;P 500 (^GSPC)</span> index.
            All values in USD. Updated daily at 19:00 SLT after the US market close.
          </p>
        </div>
        {isAdmin && (
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={onRefresh}
              disabled={refreshing || backfilling}
              className="gap-1.5"
              data-testid="us-track-record-refresh"
            >
              {refreshing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCcw className="h-3.5 w-3.5" />}
              Refresh
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={onBackfill}
              disabled={refreshing || backfilling}
              className="gap-1.5"
              data-testid="us-track-record-backfill"
            >
              {backfilling ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Database className="h-3.5 w-3.5" />}
              Rebuild from history
            </Button>
          </div>
        )}
      </div>

      {/* Data maturing notice — first 90 days of data are statistically thin */}
      {s.data_maturing && (
        <Alert
          className="border-[hsl(var(--info))]/40 bg-[hsl(var(--info))]/5"
          data-testid="us-track-record-data-maturing-notice"
        >
          <AlertCircle className="h-4 w-4 text-[hsl(var(--info))]" />
          <AlertDescription className="text-xs leading-relaxed">
            <span className="font-semibold">US track record started {fmtDate(s.launch_date)}</span>
            {s.days_since_launch != null && (
              <> — {s.days_since_launch} day{s.days_since_launch === 1 ? "" : "s"} of data so far. </>
            )}
            Meaningful accuracy data available after 90+ days of signals — until then, treat
            return / accuracy figures as preliminary. (Same situation as the CSE track record at launch.)
          </AlertDescription>
        </Alert>
      )}

      {/* Summary stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <SummaryTile
          label="Active BUY / STRONG BUY"
          value={`${s.active_signals ?? "—"}`}
          sub={`${s.strong_buy_active ?? 0} STRONG · ${s.buy_active ?? 0} BUY`}
          icon={TrendingUp}
          testid="us-track-record-kpi-active"
        />
        <SummaryTile
          label="Avg. return (active)"
          value={fmtPct(s.avg_return_active_pct)}
          sub={`vs S&P 500 ${fmtPct(s.avg_sp500_return_pct)}`}
          icon={BarChart3}
          tone={s.avg_return_active_pct}
          testid="us-track-record-kpi-return"
        />
        <SummaryTile
          label="Accuracy (price up)"
          value={s.accuracy_price_up_pct != null ? `${s.accuracy_price_up_pct.toFixed(1)}%` : "—"}
          sub={`Active: ${s.accuracy_price_up_active_pct != null ? `${s.accuracy_price_up_active_pct.toFixed(1)}%` : "—"}`}
          icon={CheckCircle2}
          testid="us-track-record-kpi-price-up"
        />
        <SummaryTile
          label="Accuracy (vs S&P 500)"
          value={s.accuracy_vs_sp500_pct != null ? `${s.accuracy_vs_sp500_pct.toFixed(1)}%` : "—"}
          sub={`Active: ${s.accuracy_vs_sp500_active_pct != null ? `${s.accuracy_vs_sp500_active_pct.toFixed(1)}%` : "—"}`}
          icon={CheckCircle2}
          testid="us-track-record-kpi-vs-sp500"
        />
      </div>

      {/* S&P 500 reference */}
      {s.sp500_current && (
        <div className="text-[11px] text-muted-foreground flex items-center gap-2">
          <Info className="h-3 w-3" />
          S&P 500 (^GSPC) benchmark: <span className="font-mono text-foreground">{fmtIndex(s.sp500_current)}</span>
          {s.sp500_current_date && <> as of {fmtDate(s.sp500_current_date)}</>}
          {" · "} {s.total_signals} tracked signals ({s.active_signals} active, {s.ended_signals} ended)
        </div>
      )}

      {/* Filters */}
      <Card className="border bg-card">
        <CardContent className="p-4">
          <div className="flex items-center gap-3 flex-wrap">
            <Tabs value={statusFilter} onValueChange={setStatusFilter}>
              <TabsList data-testid="us-track-record-status-tabs">
                <TabsTrigger value="active" data-testid="us-track-record-tab-active">Active</TabsTrigger>
                <TabsTrigger value="ended" data-testid="us-track-record-tab-ended">Ended</TabsTrigger>
                <TabsTrigger value="all" data-testid="us-track-record-tab-all">All</TabsTrigger>
              </TabsList>
            </Tabs>
            <Select value={signalFilter} onValueChange={setSignalFilter}>
              <SelectTrigger className="w-[180px]" data-testid="us-track-record-signal-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All signals</SelectItem>
                <SelectItem value="STRONG_BUY">STRONG BUY only</SelectItem>
                <SelectItem value="BUY">BUY only</SelectItem>
              </SelectContent>
            </Select>
            <div className="relative flex-1 min-w-[220px] max-w-[400px]">
              <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search symbol, company, sector…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
                data-testid="us-track-record-search"
              />
            </div>
            <div className="text-xs text-muted-foreground ml-auto">
              {filtered.length} rows
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="border bg-card">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center gap-2 p-6 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading US track record…
            </div>
          ) : error ? (
            <div className="p-6 text-sm text-[hsl(var(--destructive))]">{error}</div>
          ) : filtered.length === 0 ? (
            <div className="p-8 text-sm text-muted-foreground text-center">
              No US records yet.
              {isAdmin && (
                <div className="mt-2 text-xs">
                  Use <span className="font-mono">Rebuild from history</span> above to seed from us_score_history, or wait for the next 19:00 SLT snapshot.
                </div>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table data-testid="us-track-record-table">
                <TableHeader>
                  <TableRow>
                    <HeaderCell label="Stock" sortKey="symbol" sortBy={sortBy} onSort={onSort} />
                    <HeaderCell label="Signal" sortKey="entry_signal" sortBy={sortBy} onSort={onSort} />
                    <HeaderCell label="Signal Date" sortKey="signal_date" sortBy={sortBy} onSort={onSort} />
                    <HeaderCell label="Signal Price" sortKey="signal_price" sortBy={sortBy} onSort={onSort} align="right" />
                    <HeaderCell label="Current" sortKey="current_price" sortBy={sortBy} onSort={onSort} align="right" />
                    <HeaderCell label="Score" sortKey="live_score" sortBy={sortBy} onSort={onSort} align="right" />
                    <HeaderCell label="Live Signal" sortKey="live_signal" sortBy={sortBy} onSort={onSort} />
                    <HeaderCell label="Valuation" sortKey="valuation_status" sortBy={sortBy} onSort={onSort} />
                    <HeaderCell label="Target" sortKey="target_price" sortBy={sortBy} onSort={onSort} align="right" />
                    <HeaderCell label="Upside" sortKey="target_upside_pct" sortBy={sortBy} onSort={onSort} align="right" />
                    <HeaderCell label="Return" sortKey="return_pct" sortBy={sortBy} onSort={onSort} align="right" />
                    <HeaderCell label="vs S&P 500" sortKey="excess_return_pct" sortBy={sortBy} onSort={onSort} align="right" />
                    <HeaderCell label="Price ↑" sortKey="correct_price_up" sortBy={sortBy} onSort={onSort} align="center" />
                    <HeaderCell label="Beat S&P" sortKey="correct_vs_sp500" sortBy={sortBy} onSort={onSort} align="center" />
                    <HeaderCell label="Days" sortKey="days_held" sortBy={sortBy} onSort={onSort} align="right" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((r) => (
                    <TableRow key={r.record_id} data-testid={`us-track-record-row-${r.symbol}`}>
                      <TableCell className="align-middle">
                        <div className="flex flex-col">
                          <span className="font-mono text-xs font-semibold">
                            <span className="mr-1">🇺🇸</span>{r.symbol}
                          </span>
                          <span className="text-[10px] text-muted-foreground truncate max-w-[220px]" title={r.company_name}>
                            {r.company_name || "—"}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-1">
                          <Badge variant="outline" className={`text-[10px] w-fit ${signalBadgeClass(r.entry_signal)}`}>
                            {normaliseSignal(r.entry_signal).replace(/_/g, " ")}
                          </Badge>
                          {!r.signal_active && (
                            <Badge variant="outline" className="text-[9px] w-fit bg-muted">
                              ENDED · {r.ended_signal ? normaliseSignal(r.ended_signal).replace(/_/g, " ") : "—"}
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-xs tabular-nums">{fmtDate(r.signal_date)}</TableCell>
                      <TableCell className="text-right font-mono text-xs">{fmtUsd(r.signal_price)}</TableCell>
                      <TableCell className="text-right font-mono text-xs">{fmtUsd(r.current_price)}</TableCell>
                      <TableCell
                        className="text-right font-mono text-xs font-semibold"
                        data-testid={`us-track-record-score-${r.symbol}`}
                      >
                        {r.live_score != null ? Math.round(r.live_score) : "—"}
                      </TableCell>
                      <TableCell>
                        {r.live_signal ? (
                          <Badge
                            variant="outline"
                            className={`text-[10px] ${signalBadgeClass(r.live_signal)}`}
                          >
                            {normaliseSignal(r.live_signal).replace(/_/g, " ")}
                          </Badge>
                        ) : (
                          <span className="text-muted-foreground text-xs">—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {r.valuation_status ? (
                          <Badge
                            variant="outline"
                            className={`text-[10px] ${VALUATION_BADGE_CLASS[r.valuation_status] || "bg-muted text-muted-foreground border-muted-foreground/30"}`}
                            title={VALUATION_LABEL[r.valuation_status] || r.valuation_status}
                          >
                            {VALUATION_LABEL[r.valuation_status] || r.valuation_status.replace(/_/g, " ")}
                          </Badge>
                        ) : (
                          <span className="text-muted-foreground text-xs">—</span>
                        )}
                      </TableCell>
                      <TableCell
                        className="text-right font-mono text-xs"
                        data-testid={`us-track-record-target-${r.symbol}`}
                      >
                        {fmtUsd(r.target_price)}
                      </TableCell>
                      <TableCell className={`text-right font-mono text-xs ${returnClass(r.target_upside_pct)}`}>
                        {fmtPct(r.target_upside_pct)}
                      </TableCell>
                      <TableCell className={`text-right font-mono text-xs font-semibold ${returnClass(r.return_pct)}`} data-testid={`us-track-record-return-${r.symbol}`}>
                        {fmtPct(r.return_pct)}
                      </TableCell>
                      <TableCell className={`text-right font-mono text-xs ${returnClass(r.excess_return_pct)}`}>
                        {fmtPct(r.excess_return_pct)}
                      </TableCell>
                      <TableCell className="text-center">
                        {r.correct_price_up == null ? <Minus className="h-3.5 w-3.5 mx-auto text-muted-foreground" />
                          : r.correct_price_up
                            ? <CheckCircle2 className="h-3.5 w-3.5 mx-auto text-[hsl(var(--success))]" />
                            : <XCircle className="h-3.5 w-3.5 mx-auto text-[hsl(var(--destructive))]" />}
                      </TableCell>
                      <TableCell className="text-center">
                        {r.correct_vs_sp500 == null ? <Minus className="h-3.5 w-3.5 mx-auto text-muted-foreground" />
                          : r.correct_vs_sp500
                            ? <CheckCircle2 className="h-3.5 w-3.5 mx-auto text-[hsl(var(--success))]" />
                            : <XCircle className="h-3.5 w-3.5 mx-auto text-[hsl(var(--destructive))]" />}
                      </TableCell>
                      <TableCell className="text-right font-mono text-xs text-muted-foreground">{r.days_held ?? "—"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="text-[10px] text-muted-foreground flex items-center gap-1.5">
        <Info className="h-3 w-3" />
        US universe: S&P 500 + admin-added custom tickers. Benchmark: ^GSPC index (NOT SPY ETF).
        {summary?.updated_at && (<> · Snapshot as of {new Date(summary.updated_at).toLocaleString("en-GB")}</>)}
      </div>
    </div>
  );
};

const SummaryTile = ({ label, value, sub, icon: Icon, tone, testid }) => {
  const toneClass = tone == null ? "" : tone > 0 ? "text-[hsl(var(--success))]" : tone < 0 ? "text-[hsl(var(--destructive))]" : "";
  return (
    <Card className="border bg-card" data-testid={testid}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="space-y-0.5">
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
            <div className={`text-2xl font-display font-semibold tabular-nums ${toneClass}`}>{value}</div>
            {sub && <div className="text-[10px] text-muted-foreground">{sub}</div>}
          </div>
          {Icon && <Icon className="h-4 w-4 text-primary shrink-0" />}
        </div>
      </CardContent>
    </Card>
  );
};

const HeaderCell = ({ label, sortKey, sortBy, onSort, align = "left" }) => {
  const active = sortBy.key === sortKey;
  const alignClass = align === "right" ? "text-right" : align === "center" ? "text-center" : "text-left";
  return (
    <TableHead className={`${alignClass} text-[10px] uppercase tracking-wide cursor-pointer select-none whitespace-nowrap`} onClick={() => onSort(sortKey)}>
      <span className="inline-flex items-center gap-1">
        {label}
        {active && (sortBy.dir === "asc" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />)}
      </span>
    </TableHead>
  );
};
